/**
 * TelcoTrack — workflow helpers **outside** the core graph (`workflow-model.ts`).
 *
 * Responsibility: UI/rollback behaviour that needs object history, not the Excel graph.
 * Does not define phases, colors, or next/previous — import those from `workflow-model.ts`.
 */

/**
 * Omezí seznam předchozích fází na **jeden** krok zpět podle historie:
 * poslední přechod **do** aktuální fáze (`statusTransitionTo` = aktuální stav), nejnovější první.
 * Bez takového záznamu v timeline vrací původní `prev` (fallback).
 */
export function filterPreviousPhasesByObjectHistoryTimeline<T extends { code: string }>(
  prev: T[],
  timeline: unknown[] | null | undefined,
  currentPhaseCode: string,
): T[] {
  const cur = (currentPhaseCode ?? '').trim();
  if (!cur || !prev.length) return prev;
  if (!Array.isArray(timeline) || timeline.length === 0) return prev;

  const norm = (s: string) => (s ?? '').trim().toUpperCase();
  const curN = norm(cur);

  const statusChanges = [...timeline]
    .filter((e) => (e as { changeType?: string })?.changeType === 'status_change')
    .sort((a, b) => {
      const ta = new Date((a as { createdAt?: string | Date })?.createdAt ?? 0).getTime();
      const tb = new Date((b as { createdAt?: string | Date })?.createdAt ?? 0).getTime();
      return tb - ta;
    });

  let immediateFrom: string | null = null;
  for (const e of statusChanges) {
    const ex = e as {
      statusTransitionTo?: string | null;
      status_transition_to?: string | null;
      statusTransitionFrom?: string | null;
      status_transition_from?: string | null;
    };
    const to = norm(String(ex.statusTransitionTo ?? ex.status_transition_to ?? ''));
    const from = String(ex.statusTransitionFrom ?? ex.status_transition_from ?? '').trim();
    if (!from || to !== curN) continue;
    immediateFrom = from;
    break;
  }

  if (!immediateFrom) return prev;

  const prevByCode = new Map(prev.map((p) => [norm(p.code), p]));
  const p = prevByCode.get(norm(immediateFrom));
  return p ? [p] : prev;
}
